/**
 * Tells a running tab when the server it is talking to has moved on to a
 * newer deploy than the JavaScript bundle it loaded with.
 *
 * A Uniflo tab is a single-page app: once it loads, nothing about routing,
 * fetching, or rendering re-fetches index.html, so a tab left open across a
 * deploy keeps running the old bundle indefinitely. This module polls
 * `GET /healthz` (the same release block AppVersionLabel and the deploy-check
 * workflow already read) and compares its commit against the one this bundle
 * was built from (`appVersion.js`). When they differ, it flips `updateAvailable`
 * and notifies subscribers — `AppUpdatePrompt` renders the toast — and, when
 * it is safe to do so without disturbing the user, reloads the tab itself.
 *
 * See docs/operations/versioning-and-build-stamps.md, "How a running tab
 * learns about a deploy".
 */
import { APP_GIT_SHA } from "./appVersion";

const INITIAL_CHECK_DELAY_MS = 60 * 1000; // ~60s after start
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // every 5 minutes
const MIN_CHECK_INTERVAL_MS = 60 * 1000; // never check more than once a minute
const HIDDEN_RECHECK_THRESHOLD_MS = 30 * 60 * 1000; // "hidden for more than 30 minutes"
const RELOAD_GUARD_MIN_INTERVAL_MS = 10 * 60 * 1000; // never auto-reload more than once per 10 minutes
const RELOAD_GUARD_STORAGE_KEY = "uniflo:appUpdateWatcher:lastAutoReloadAt";
const MIN_COMPARABLE_SHA_LENGTH = 7; // compare on a common prefix of at least this many chars

function initialState() {
  return {
    runningSha: APP_GIT_SHA || null,
    updateAvailable: false,
    liveRelease: null,
    lastCheckAt: 0,
    checkCount: 0,
  };
}

let state = initialState();
let listeners = new Set();
let started = false;
let initialTimer = null;
let intervalTimer = null;
let hiddenSince = null;
let lastPathname = null;

function isEnabled() {
  // "if the running build has no commit, e.g. local dev, the watcher does
  // nothing" — there is nothing meaningful to compare against.
  return Boolean(state.runningSha);
}

function normalizeSha(value) {
  return `${value || ""}`.trim().toLowerCase();
}

/**
 * Case-insensitive comparison over the common prefix length of both SHAs
 * (>= 7 chars). Handles a short SHA compared against a full 40-char one, and
 * refuses to call two values "different" from too little overlap to compare
 * confidently.
 */
export function shasDiffer(runningSha, liveSha) {
  const a = normalizeSha(runningSha);
  const b = normalizeSha(liveSha);
  if (!a || !b) return false; // "ignore a live response with no sha"
  const commonLength = Math.min(a.length, b.length);
  if (commonLength < MIN_COMPARABLE_SHA_LENGTH) return false;
  return a.slice(0, commonLength) !== b.slice(0, commonLength);
}

export function getState() {
  return { ...state };
}

export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function notify() {
  listeners.forEach((listener) => {
    try {
      listener(getState());
    } catch (_error) {
      // A subscriber's own bug must never break the watcher.
    }
  });
}

function isEditableElement(element) {
  if (!element) return false;
  const tag = `${element.tagName || ""}`.toLowerCase();
  if (tag === "input" || tag === "textarea") return true;
  // `isContentEditable` is the real signal in a real browser, but jsdom
  // (this project's test environment) does not implement it, so fall back to
  // reading the attribute directly — `contenteditable=""` and
  // `contenteditable="true"` both mean editable per the HTML spec.
  if (element.isContentEditable) return true;
  const attr = typeof element.getAttribute === "function" ? element.getAttribute("contenteditable") : null;
  return attr === "" || attr === "true";
}

/**
 * A dialog is open, or the user is mid-keystroke in a field — reloading now
 * would drop their work or yank a modal out from under them. `role="dialog"`
 * and `role="alertdialog"` cover both ConfirmDialogHost and any other modal
 * built to the same contract.
 */
export function canSafelyReloadNow(doc = document) {
  if (!doc || typeof doc.querySelector !== "function") return false;
  if (doc.querySelector('[role="dialog"],[role="alertdialog"]')) return false;
  if (isEditableElement(doc.activeElement)) return false;
  return true;
}

/**
 * At most one auto-reload per 10 minutes, guarded through sessionStorage so
 * it survives the reload itself (an in-memory flag would not). Wrapped in
 * try/catch end to end: a sessionStorage failure (private mode, quota, a
 * hostile polyfill) must fail CLOSED — refuse the reload — so a bad deploy or
 * a broken storage API can never turn this into a reload loop.
 */
function tryConsumeReloadGuard(storage) {
  try {
    const store = storage || window.sessionStorage;
    const raw = store.getItem(RELOAD_GUARD_STORAGE_KEY);
    const last = raw ? Number(raw) : 0;
    if (Number.isFinite(last) && last > 0 && Date.now() - last < RELOAD_GUARD_MIN_INTERVAL_MS) {
      return false;
    }
    store.setItem(RELOAD_GUARD_STORAGE_KEY, String(Date.now()));
    return true;
  } catch (_error) {
    return false;
  }
}

/**
 * The one place that actually reloads the tab outside the explicit Reload
 * button. Only fires when every guard agrees: an update is known to be
 * pending, no dialog is open, no input has focus, and the 10-minute
 * sessionStorage guard has not just fired. Exposed for the debug hook so the
 * project manager can prove the guard live without waiting on the schedule.
 */
export function attemptSafeReload({ doc = document, win = window, storage } = {}) {
  if (!state.updateAvailable) return false;
  if (!canSafelyReloadNow(doc)) return false;
  if (!tryConsumeReloadGuard(storage)) return false;
  win.location.reload();
  return true;
}

async function checkNow({ force = false } = {}) {
  if (!isEnabled()) return;
  if (typeof navigator !== "undefined" && navigator.onLine === false) return;

  const now = Date.now();
  if (!force && state.lastCheckAt && now - state.lastCheckAt < MIN_CHECK_INTERVAL_MS) return;
  // Recorded before the fetch even settles: a failure must not be retried
  // faster than a success would have been.
  state.lastCheckAt = now;
  state.checkCount += 1;

  try {
    const response = await fetch("/healthz", {
      cache: "no-store",
      credentials: "omit",
    });
    if (!response || !response.ok) return;
    const data = await response.json();
    const live = data && data.release;
    if (!live) return;
    const liveSha = live.gitShortSha || live.gitSha || null;
    if (!liveSha) return; // "ignore a live response with no sha"

    state.liveRelease = live;
    if (!state.updateAvailable && shasDiffer(state.runningSha, liveSha)) {
      state.updateAvailable = true;
      notify();
    }
  } catch (_error) {
    // Silent by design — the next scheduled or triggered check will try again.
  }
}

function handleVisibilityChange() {
  if (typeof document === "undefined") return;
  if (document.hidden) {
    hiddenSince = Date.now();
    return;
  }

  const hiddenFor = hiddenSince ? Date.now() - hiddenSince : 0;
  hiddenSince = null;
  checkNow();
  if (state.updateAvailable && hiddenFor > HIDDEN_RECHECK_THRESHOLD_MS) {
    attemptSafeReload();
  }
}

/**
 * Called on every in-app pathname change. Triggers a (throttled) check, and
 * — this is the safe-auto-reload path — if an update is already known to be
 * pending, reloads right at the navigation instead of letting it complete
 * against stale code, provided nothing on screen would be disturbed by it.
 */
export function notifyRouteChange(pathname) {
  if (pathname === lastPathname) return;
  const isFirstRoute = lastPathname === null;
  lastPathname = pathname;
  if (isFirstRoute) return; // initial mount is not a navigation
  checkNow();
  if (state.updateAvailable) {
    attemptSafeReload();
  }
}

export function start() {
  if (started) return;
  started = true;
  if (!isEnabled()) return;

  initialTimer = setTimeout(() => {
    checkNow();
  }, INITIAL_CHECK_DELAY_MS);
  intervalTimer = setInterval(() => {
    checkNow();
  }, CHECK_INTERVAL_MS);

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", handleVisibilityChange);
  }
}

export function stop() {
  started = false;
  if (initialTimer) clearTimeout(initialTimer);
  if (intervalTimer) clearInterval(intervalTimer);
  initialTimer = null;
  intervalTimer = null;
  if (typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  }
}

export function resetAppUpdateWatcherForTests() {
  stop();
  state = initialState();
  listeners = new Set();
  hiddenSince = null;
  lastPathname = null;
}

// A harmless console escape hatch for live QA: prove the watcher sees a new
// deploy, and prove the reload + loop guard work, without waiting out the
// schedule. `checkNow` here always forces a fresh fetch (bypassing only the
// once-a-minute throttle for this one manual call); `attemptSafeReload` runs
// through every real guard and only reloads when they all agree.
if (typeof window !== "undefined") {
  window.__UNIFLO_UPDATE__ = {
    getState,
    checkNow: () => checkNow({ force: true }),
    notifyRouteChange,
    attemptSafeReload: () => attemptSafeReload(),
  };
}
